import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useSupabase } from '../contexts/SupabaseContext';

export interface Conversation {
  id: string;
  topic: string | null;
  order_id: string | null;
  created_at: string;
  updated_at: string;
  status: 'active' | 'archived' | 'closed';
  participants: Participant[];
  last_message?: Message;
  unread_count?: number;
}

export interface Participant {
  id: string;
  conversation_id: string;
  user_id: string;
  role: 'buyer' | 'seller' | 'agent';
  joined_at: string;
  last_read_at: string;
  user?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
  attachment_url: string | null;
  attachment_type: string | null;
  sender?: {
    first_name: string;
    last_name: string;
  };
}

export const useChat = () => {
  const { user } = useSupabase();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch user's conversations
  const fetchConversations = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Get conversations where user is a participant
      const { data: participantData, error: participantError } = await supabase
        .from('participants')
        .select('conversation_id')
        .eq('user_id', user.id);
      
      if (participantError) throw participantError;
      
      if (participantData.length === 0) {
        setConversations([]);
        setLoading(false);
        return;
      }
      
      const conversationIds = participantData.map(p => p.conversation_id);
      
      // Get conversations with last message
      const { data: conversationsData, error: conversationsError } = await supabase
        .from('conversations')
        .select(`
          *,
          participants:participants(
            id,
            user_id,
            role,
            joined_at,
            last_read_at,
            users:user_id(first_name, last_name, email)
          )
        `)
        .in('id', conversationIds)
        .order('updated_at', { ascending: false });
      
      if (conversationsError) throw conversationsError;
      
      // Get last message for each conversation
      const conversationsWithLastMessage = await Promise.all(
        conversationsData.map(async (conversation) => {
          const { data: lastMessageData, error: lastMessageError } = await supabase
            .from('messages')
            .select(`
              *,
              sender:sender_id(first_name, last_name)
            `)
            .eq('conversation_id', conversation.id)
            .order('created_at', { ascending: false })
            .limit(1);
          
          if (lastMessageError) throw lastMessageError;
          
          // Get unread count
          const { data: unreadData, error: unreadError } = await supabase
            .rpc('get_unread_message_count', { user_uuid: user.id });
          
          if (unreadError) throw unreadError;
          
          return {
            ...conversation,
            last_message: lastMessageData[0] || null,
            unread_count: unreadData || 0
          };
        })
      );
      
      setConversations(conversationsWithLastMessage);
      
      // Calculate total unread count
      const totalUnread = conversationsWithLastMessage.reduce(
        (sum, conv) => sum + (conv.unread_count || 0), 
        0
      );
      setUnreadCount(totalUnread);
      
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId: string) => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Get messages
      const { data: messagesData, error: messagesError } = await supabase
        .from('messages')
        .select(`
          *,
          sender:sender_id(first_name, last_name)
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
      
      if (messagesError) throw messagesError;
      
      setMessages(messagesData);
      
      // Update last_read_at for the user
      await supabase
        .from('participants')
        .update({ last_read_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id);
      
      // Mark messages as read
      await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('conversation_id', conversationId)
        .neq('sender_id', user.id)
        .eq('is_read', false);
      
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Send a message
  const sendMessage = useCallback(async (
    conversationId: string, 
    content: string,
    attachmentUrl?: string,
    attachmentType?: string
  ) => {
    if (!user) return null;
    
    try {
      const { data, error } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          content,
          attachment_url: attachmentUrl || null,
          attachment_type: attachmentType || null
        })
        .select()
        .single();
      
      if (error) throw error;
      
      return data;
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      return null;
    }
  }, [user]);

  // Create a new conversation
  const createConversation = useCallback(async (
    participants: { user_id: string, role: 'buyer' | 'seller' | 'agent' }[],
    topic?: string,
    orderId?: string
  ) => {
    if (!user) return null;
    
    try {
      // First, create the conversation
      const { data: conversationData, error: conversationError } = await supabase
        .from('conversations')
        .insert({
          topic: topic || null,
          order_id: orderId || null
        })
        .select()
        .single();
      
      if (conversationError) throw conversationError;
      
      // Add current user as participant if not already included
      const currentUserIncluded = participants.some(p => p.user_id === user.id);
      
      const participantsToAdd = currentUserIncluded 
        ? participants 
        : [...participants, { user_id: user.id, role: 'buyer' as const }];
      
      // Add participants
      const participantsWithConversationId = participantsToAdd.map(p => ({
        ...p,
        conversation_id: conversationData.id
      }));
      
      const { error: participantsError } = await supabase
        .from('participants')
        .insert(participantsWithConversationId);
      
      if (participantsError) throw participantsError;
      
      return conversationData;
    } catch (err) {
      console.error('Error creating conversation:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      return null;
    }
  }, [user]);

  // Join a conversation (for agents)
  const joinConversation = useCallback(async (
    conversationId: string,
    role: 'agent'
  ) => {
    if (!user) return false;
    
    try {
      const { error } = await supabase
        .from('participants')
        .insert({
          conversation_id: conversationId,
          user_id: user.id,
          role
        });
      
      if (error) throw error;
      
      return true;
    } catch (err) {
      console.error('Error joining conversation:', err);
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
      return false;
    }
  }, [user]);

  // Check if a conversation exists between users
  const findConversationBetweenUsers = useCallback(async (userIds: string[]) => {
    try {
      // For each user, get their conversations
      const userConversationsPromises = userIds.map(userId => 
        supabase
          .from('participants')
          .select('conversation_id')
          .eq('user_id', userId)
      );
      
      const userConversationsResults = await Promise.all(userConversationsPromises);
      
      // Extract conversation IDs for each user
      const userConversationSets = userConversationsResults.map((result, index) => {
        if (result.error) {
          console.error(`Error fetching conversations for user ${userIds[index]}:`, result.error);
          return new Set<string>();
        }
        return new Set(result.data.map(p => p.conversation_id));
      });
      
      // Find common conversation IDs
      if (userConversationSets.length === 0) return null;
      
      let commonConversations = [...userConversationSets[0]];
      
      for (let i = 1; i < userConversationSets.length; i++) {
        commonConversations = commonConversations.filter(id => 
          userConversationSets[i].has(id)
        );
      }
      
      if (commonConversations.length === 0) return null;
      
      // Get the most recent common conversation
      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .in('id', commonConversations)
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();
      
      if (error) return null;
      
      return data;
    } catch (err) {
      console.error('Error finding conversation between users:', err);
      return null;
    }
  }, []);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!user) return;
    
    // Subscribe to new messages
    const messagesSubscription = supabase
      .channel('messages-channel')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: currentConversation 
          ? `conversation_id=eq.${currentConversation.id}` 
          : undefined
      }, (payload) => {
        // If we're in a conversation, add the new message
        if (currentConversation && payload.new.conversation_id === currentConversation.id) {
          setMessages(prev => [...prev, payload.new as Message]);
          
          // Mark as read if not from current user
          if (payload.new.sender_id !== user.id) {
            supabase
              .from('messages')
              .update({ is_read: true })
              .eq('id', payload.new.id)
              .then();
          }
        }
        
        // Refresh conversations to update last message
        fetchConversations();
      })
      .subscribe();
    
    // Subscribe to conversation updates
    const conversationsSubscription = supabase
      .channel('conversations-channel')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'conversations'
      }, () => {
        fetchConversations();
      })
      .subscribe();
    
    return () => {
      supabase.removeChannel(messagesSubscription);
      supabase.removeChannel(conversationsSubscription);
    };
  }, [user, currentConversation, fetchConversations]);

  // Initial fetch of conversations
  useEffect(() => {
    if (user) {
      fetchConversations();
    }
  }, [user, fetchConversations]);

  return {
    conversations,
    currentConversation,
    setCurrentConversation,
    messages,
    loading,
    error,
    unreadCount,
    fetchConversations,
    fetchMessages,
    sendMessage,
    createConversation,
    joinConversation,
    findConversationBetweenUsers
  };
};